import { useFormContext } from 'react-hook-form'
import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { ReviewServicesCard } from '@/features/services'
import { useBusinessCategories } from '../../hooks/useBusinessCategories'
import { useCountries, findLocationNames } from '@/shared/hooks/useCountries'
import type { CreateBusinessFormValues } from '../../schemas/createBusinessSchema'

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right font-medium text-ink">{value || '—'}</span>
    </div>
  )
}

function EditLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-semibold text-brand hover:underline"
    >
      Editar
    </button>
  )
}

interface ReviewStepProps {
  onEdit: (stepId: string) => void
}

export function ReviewStep({ onEdit }: ReviewStepProps) {
  const { getValues } = useFormContext<CreateBusinessFormValues>()
  const values = getValues()

  const { categories } = useBusinessCategories()
  const selectedCategoryNames =
    values.businessCategoryIds?.length
      ? categories
          .filter((c) => values.businessCategoryIds!.includes(c.id))
          .map((c) => c.name)
          .join(', ')
      : ''

  const { countries } = useCountries()
  const location = findLocationNames(countries, values.address)

  return (
    <div className="grid gap-4">
      <p className="text-sm text-ink-muted">
        Revisa los datos. Al confirmar se creará el negocio y aparecerá en tu lista.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Datos generales</CardTitle>
          <EditLink onClick={() => onEdit('general')} />
        </CardHeader>
        <div className="grid gap-2">
          <Row label="Nombre legal" value={values.legalName} />
          <Row label="Descripción" value={values.description} />
          <Row label="Logo" value={values.logo} />
          <Row label="Categorías" value={selectedCategoryNames} />
        </div>
      </Card>

      <ReviewServicesCard
        selectedIds={values.packageServiceIds ?? []}
        onEdit={() => onEdit('services')}
      />

      <Card>
        <CardHeader>
          <CardTitle>Dirección</CardTitle>
          <EditLink onClick={() => onEdit(values.useSameOrgAddress ? 'general' : 'address')} />
        </CardHeader>
        {values.useSameOrgAddress ? (
          <p className="text-sm text-ink-muted">
            Usa la dirección principal de tu organización.
          </p>
        ) : (
          <div className="grid gap-2">
            <Row label="Calle" value={values.address.street1} />
            <Row label="Ciudad" value={values.address.city} />
            <Row label="País" value={location.countryName} />
            <Row label="Provincia" value={location.provinceName} />
            <Row label="Municipio" value={location.municipalityName} />
          </div>
        )}
      </Card>
    </div>
  )
}
